import React, { useState, useEffect } from 'react';
import { Search, MessageCircle, User } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import PatientChatInterface from '../../components/Chat/PatientChatInterface';
import { updateChatList } from '../../redux/doctorSlice';
import { useDispatch, useSelector } from 'react-redux';
import { getSocket } from '../../socket';
import { formatDateChat, formatDateHeader } from '../../utils/dateHeader';
import CommonLoader from '../../components/CommonLoader';
import ErrorPage from '../../components/ErrorPage';
import { getDoctorMissingSetupItems } from '../../utils/doctorSetupStatus';
import errorimg from '../../assets/img/colorkit (1).svg';
import { getFileURL } from '../../utils/fileURL';

const PatientChatPage = () => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchError, setSearchError] = useState('');
    const userRole = localStorage.getItem('Role');
    const doctorId = localStorage.getItem('ID');
    const { patientListChat, selectedDoctor } = useSelector((state) => state.doctor);
    const [socketState, setSocketState] = useState('connecting'); // 'connecting', 'connected', 'error'
    const [socketError, setSocketError] = useState(null);

    const MAX_SEARCH_LENGTH = 50;

    const formatTime = (date) => {
        if (!date) return "";

        // Convert to Date object if string
        const d = typeof date === "string" ? new Date(date) : date;

        // Invalid date guard
        if (isNaN(d.getTime())) return "";

        const now = new Date();
        const diff = now - d;
        const minutes = Math.floor(diff / 60000);
        const hours = Math.floor(diff / 3600000);
        const days = Math.floor(diff / 86400000);

        if (minutes < 1) return t('chat.time.justNow');
        if (minutes < 60) return t('chat.time.minutesAgo', { minutes });
        if (hours < 24) return t('chat.time.hoursAgo', { hours });
        if (days < 7) return t('chat.time.daysAgo', { days });

        return d.toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', { month: 'short', day: 'numeric' });
    };


    useEffect(() => {
        // Check authentication first
        const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
        if (!isAuthenticated || !doctorId) {
            setSocketState('error');
            setSocketError({
                message: t('patientChatPage.pleaseLoginToAccessChat'),
                status: 401,
            });
            return;
        }

        // Get socket only if authenticated
        const chatSocket = getSocket();
        if (!chatSocket) {
            setSocketState('error');
            setSocketError({
                message: t('patientChatPage.unableToInitializeChat'),
                status: 401,
            });
            return;
        }

        // Set connecting state
        setSocketState('connecting');
        setSocketError(null);

        // Handle socket connection
        const handleConnect = () => {
            console.log("Socket connected");
            setSocketState('connected');
        };

        // Handle socket connection errors
        const handleConnectError = (err) => {
            console.error("Socket connection error:", err);
            setSocketState('error');
            setSocketError({
                message: err.message || t('patientChatPage.failedToConnect'),
                status: err.status || 500,
            });
        };

        // Handle socket errors
        const handleError = (error) => {
            console.error("Socket error:", error);
            setSocketState('error');
            setSocketError({
                message: error.message || t('patientChatPage.chatServiceError'),
                status: error.status || 500,
            });
        };

        // Handle socket disconnection
        const handleDisconnect = (reason) => {
            console.log("Socket disconnected:", reason);
            if (reason === 'io server disconnect' || reason === 'transport close' || reason === 'transport error') {
                setSocketState('error');
                setSocketError({
                    message: t('patientChatPage.connectionLost'),
                    status: 500,
                });
            }
        };

        // Listen for initial chat list (sent automatically on connection)
        const handleChatListReady = (response) => {
            console.log("chatListReady received:", response);
            if (response.success && response.data) {
                // Update the chat list with all patients
                response.data.forEach(patient => {
                    // Map chatListReady structure to chatListUpdate structure
                    const mappedPatient = {
                        patientId: patient.patientId,
                        patient: {
                            name: patient.name,
                            surname: patient.surname,
                            image: patient.image,
                            patientId: patient.patientId,
                            isOnline: patient.isOnline,
                            lastSeen: patient.lastSeen,
                        },
                        lastMessage: patient.lastMessage,
                        unread: patient.unread || 0,
                        unreadForDoctor: patient.unreadForDoctor !== undefined ? patient.unreadForDoctor : patient.unread || 0,
                        unreadForPatient: patient.unreadForPatient || 0,
                    };
                    dispatch(updateChatList(mappedPatient));
                });
            }
        };

        // Listen for chat list updates (when new messages arrive or status changes)
        const handleChatListUpdate = (updatedChat) => {
            console.log("chatListUpdate received:", updatedChat);
            dispatch(updateChatList(updatedChat));
        };

        // Register socket event listeners
        chatSocket.on('connect', handleConnect);
        chatSocket.on('connect_error', handleConnectError);
        chatSocket.on('error', handleError);
        chatSocket.on('disconnect', handleDisconnect);
        chatSocket.on('chatListReady', handleChatListReady);
        chatSocket.on('chatListUpdate', handleChatListUpdate);

        // Connect/reconnect socket - this will trigger automatic chatListReady event
        // If socket is already connected (e.g., from global connection in SupportSocketContext),
        // we need to reconnect so the server sends chatListReady again with the patient list
        if (chatSocket.connected) {
            chatSocket.disconnect();
        }
        chatSocket.connect();

        return () => {
            if (chatSocket) {
                chatSocket.off('connect', handleConnect);
                chatSocket.off('connect_error', handleConnectError);
                chatSocket.off('error', handleError);
                chatSocket.off('disconnect', handleDisconnect);
                chatSocket.off('chatListReady', handleChatListReady);
                chatSocket.off('chatListUpdate', handleChatListUpdate);
            }
        }
    }, [doctorId, dispatch]);

    // Validate search input
    const validateSearchInput = (value) => {
        // Remove any potentially dangerous characters
        const sanitized = value.replace(/[<>]/g, '');

        // Check length
        if (sanitized.length > MAX_SEARCH_LENGTH) {
            setSearchError(t('patientChatPage.searchExceedsMaxLength', { maxLength: MAX_SEARCH_LENGTH }));
            return false;
        }

        // Check for only whitespace
        if (sanitized.trim().length === 0 && sanitized.length > 0) {
            setSearchError(t('patientChatPage.searchOnlySpaces'));
            return false;
        }

        setSearchError('');
        return true;
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;

        // Sanitize input - remove dangerous characters
        const sanitized = value.replace(/[<>]/g, '');
        const finalValue = sanitized.length > 50 ? sanitized.substring(0, 50) : sanitized;

        // Always update searchQuery so the input stays in sync
        setSearchQuery(finalValue);

        // Validate input
        validateSearchInput(finalValue);
    };

    // Filter patients based on search
    const filteredPatients = patientListChat.filter(patient =>
        patient.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        patient.patientId?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    console.log("filteredpatient", filteredPatients)

    const handlePatientSelect = (patient) => {
        setSelectedPatient(patient);
    };

    // Show error page if socket connection failed
    if (socketState === 'error' && socketError) {
        return <ErrorPage error={socketError} pageName={t('chat.patients', 'Chat Page')} />;
    }

    // Show loader while connecting
    if (socketState === 'connecting') {
        return (
            <div className="h-full flex items-center justify-center bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-lg" data-testid="patient-chat-page-loading">
                <CommonLoader />
            </div>
        );
    }

    // Profile completion validation for doctors
    if (userRole === 'doctor') {
        // Get missing setup items using utility function
        const missingItemKeys = getDoctorMissingSetupItems(userRole, selectedDoctor);
        const missingItems = missingItemKeys.map(key => t(key));

        // If any required items are missing, show restriction page
        if (missingItems.length > 0) {
            return (
                <div className="h-full bg-white rounded-2xl p-4 sm:p-6 md:p-8 lg:p-10 flex items-center justify-center" data-testid="patient-chat-page-profile-validation">
                    <div className="text-center max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl">
                        <img src={errorimg} alt={t('profileValidation.altText')} className="mx-auto w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 lg:w-56 lg:h-56 xl:w-64 xl:h-64 object-contain" data-testid="patient-chat-page-profile-error-image" />
                        <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#68c0c0] mb-3 sm:mb-4" data-testid="patient-chat-page-profile-validation-title">
                            {t('profileValidation.title')}
                        </h2>
                        <p className="text-sm sm:text-base md:text-lg text-gray-800 mb-2 sm:mb-3 md:mb-4" data-testid="patient-chat-page-profile-validation-message">
                            {t('profileValidation.message')}
                        </p>
                        <div className="text-left bg-red-50 border border-red-200 rounded-lg p-4 mb-4 sm:mb-6 md:mb-8" data-testid="patient-chat-page-missing-items-container">
                            <p className="text-xs sm:text-sm md:text-base text-gray-700 font-semibold mb-2" data-testid="patient-chat-page-missing-items-label">
                                {t('profileValidation.missingItems')}:
                            </p>
                            <ul className="list-disc list-inside space-y-1 text-xs sm:text-sm md:text-base text-gray-600" data-testid="patient-chat-page-missing-items-list">
                                {missingItems.map((item, index) => (
                                    <li key={index} data-testid={`patient-chat-page-missing-item-${index}`}>{item}</li>
                                ))}
                            </ul>
                        </div>
                        <button
                            onClick={() => navigate('/doctorProfile')}
                            className="px-3 py-1.5 sm:px-4 sm:py-2 md:px-5 md:py-2.5 bg-gradient-to-br from-[#4ABDBD] to-[#3A9B9B] text-white font-semibold rounded-lg shadow-sm text-xs sm:text-sm md:text-base transition-all hover:from-[#3aa8a8] hover:to-[#2a7b7b] focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] focus:ring-opacity-50 cursor-pointer"
                            data-testid="patient-chat-page-go-to-profile-button"
                        >
                            {t('profileValidation.goToProfile')}
                        </button>
                    </div>
                </div>
            );
        }
    }

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="h-full flex bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-lg w-full overflow-hidden"
            data-testid="patient-chat-page-container"
        >
            {/* Left Sidebar - Patient List */}
            <motion.div
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.4 }}
                className="w-96 bg-white border-r-4 border-gray-200 flex flex-col shadow-sm"
                data-testid="patient-chat-sidebar"
            >
                {/* Header */}
                <div className="p-5 border-b border-gray-200 bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B]" data-testid="patient-chat-sidebar-header">
                    <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2" data-testid="patient-chat-sidebar-title">
                        <MessageCircle className="w-6 h-6" data-testid="patient-chat-sidebar-icon" />
                        {t('chat.patients')}
                    </h2>
                    {/* Search */}
                    <div className="relative" data-testid="patient-chat-search-container">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600 z-10" size={18} data-testid="patient-chat-search-icon" />
                        <input
                            type="text"
                            placeholder={t('chat.searchPatients')}
                            value={searchQuery}
                            onChange={handleSearchChange}
                            maxLength={MAX_SEARCH_LENGTH}
                            className={`w-full pl-10 pr-4 py-2.5 border rounded-xl focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50 bg-white/90 backdrop-blur-sm text-gray-800 placeholder-gray-500 ${searchError
                                ? 'border-red-300 bg-red-50/90'
                                : 'border-white/30'
                                }`}
                            data-testid="patient-chat-search-input"
                        />
                        {searchError && (
                            <p className="mt-1 text-xs text-red-200 absolute -bottom-5 left-0" data-testid="patient-chat-search-error">{searchError}</p>
                        )}
                    </div>
                </div>

                {/* Patient List */}
                <div className="flex-1 overflow-y-auto custom-scrollbar bg-gray-50/50" data-testid="patient-chat-list-container">
                    {filteredPatients.length === 0 ? (
                        <div className="p-8 text-center text-gray-500" data-testid="patient-chat-no-patients">
                            <MessageCircle className="mx-auto mb-2 text-gray-400" size={48} data-testid="patient-chat-no-patients-icon" />
                            <p data-testid="patient-chat-no-patients-text">{t('chat.noPatientsFound')}</p>
                        </div>
                    ) : (
                        <div className="p-3" data-testid="patient-chat-list">
                            <AnimatePresence>
                                {filteredPatients.map((patient, index) => (
                                    <motion.button
                                        key={patient.patientId}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: index * 0.05 }}
                                        onClick={() => handlePatientSelect(patient)}
                                        className={`w-full p-4 mb-2 rounded-xl transition-all text-left relative overflow-hidden ${selectedPatient?.patientId === patient.patientId
                                            ? 'bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] text-white shadow-lg shadow-[#4ABDBD]/30'
                                            : 'bg-white hover:bg-gray-50 text-gray-800 shadow-sm hover:shadow-md'
                                            }`}
                                        data-testid={`patient-chat-card-${patient.patientId}`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="relative flex-shrink-0">
                                                {(patient?.image || patient?.photo) ? (
                                                    <img
                                                        src={getFileURL(patient.image || patient.photo)}
                                                        alt={patient.name?.charAt(0).toUpperCase()}
                                                        className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-md"
                                                    />
                                                ) : (
                                                    <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#4ebfbf] to-[#3A9B9B] flex items-center justify-center border-2 border-white shadow-md">
                                                        <span className="text-white text-xl font-semibold">
                                                            {patient?.name?.charAt(0)?.toUpperCase()}
                                                        </span>
                                                    </div>
                                                )}

                                                {patient.isOnline && (
                                                    <span className="absolute bottom-0 right-0 w-4 h-4 bg-green-500 border-2 border-white rounded-full"></span>
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center justify-between gap-2 mb-1">
                                                    <div className={`font-semibold truncate ${selectedPatient?.patientId === patient.patientId
                                                        ? 'text-white'
                                                        : 'text-gray-800'
                                                        }`}>
                                                        {patient?.name}
                                                    </div>
                                                    <div className={`text-xs ${selectedPatient?.patientId === patient.patientId
                                                        ? 'text-white/70'
                                                        : 'text-gray-400'
                                                        }`}>
                                                        {patient.lastMessage?.createdAt ? formatDateChat(patient.lastMessage?.createdAt) : t('patientChatPage.clickToStartChat')}
                                                        {/* {formatDateChat(patient.lastMessage?.createdAt)} */}
                                                    </div>

                                                </div>
                                                <div className="flex items-center justify-between gap-2 mb-1">

                                                    <div className={`text-xs truncate ${selectedPatient?.patientId === patient.patientId
                                                        ? 'text-white/90'
                                                        : 'text-gray-600'
                                                        }`}>
                                                        {patient.lastMessage?.message}
                                                    </div>
                                                    {(patient?.unviewedCount > 0 || patient?.unreadForDoctor > 0 || patient?.unread > 0) && (
                                                        <motion.span
                                                            initial={{ scale: 0 }}
                                                            animate={{ scale: 1 }}
                                                            className={`flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-bold ${selectedPatient?.patientId === patient.patientId
                                                                ? 'bg-white/30 text-white'
                                                                : 'bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] text-white'
                                                                }`}
                                                        >
                                                            {(() => {
                                                                const count = patient?.unviewedCount || patient?.unreadForDoctor || patient?.unread || 0;
                                                                return count > 99 ? '99+' : count;
                                                            })()}
                                                        </motion.span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </motion.button>
                                ))}
                            </AnimatePresence>
                        </div>
                    )}
                </div>
            </motion.div>

            {/* Right Side - Chat Screen */}
            <motion.div
                initial={{ x: 20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.1 }}
                className="flex-1 flex flex-col bg-white"
                data-testid="patient-chat-main-container"
            >
                {selectedPatient ? (
                    <PatientChatInterface socket={getSocket()} patient={selectedPatient} doctorId={doctorId} />
                ) : (
                    <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-gray-50 to-white" data-testid="patient-chat-empty-state">
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.3 }}
                            className="text-center"
                        >
                            <motion.div
                                animate={{ rotate: [0, 10, -10, 0] }}
                                transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
                            >
                                <MessageCircle className="mx-auto mb-4 text-gray-400" size={64} data-testid="patient-chat-empty-icon" />
                            </motion.div>
                            <h3 className="text-xl font-semibold text-gray-700 mb-2" data-testid="patient-chat-empty-title">{t('chat.selectPatient')}</h3>
                            <p className="text-gray-500" data-testid="patient-chat-empty-message">{t('chat.selectPatientMessage')}</p>
                        </motion.div>
                    </div>
                )}
            </motion.div>
        </motion.div>
    );
};

export default PatientChatPage;
